import React from 'react'
import { node, string } from 'prop-types'
import { format, parseISO } from 'date-fns'

import Icon from '../Icon'
import SmartLink from '../SmartLink'
import Type from '../Type'
import Hide from '../Hide'

import styles from './ArticleEventInfo.module.scss'

import ArticleEventRegistration from '@/component/Primitive/ArticleEventRegistration'

const InfoBlock = ({ children, heading, ctaText, ctaUrl, icon }) => (
  <div className={styles.InfoBlock}>
    <Type
      size={['heading8', 'heading6']}
      weight="bold"
      className={styles.InfoBlockHeading}
    >
      {heading}
    </Type>
    {children}
    {ctaUrl && (
      <SmartLink target="_blank" className={styles.CTA} href={ctaUrl}>
        <Type size="heading8" weight="medium" as="span">
          {ctaText}
        </Type>

        <Icon className={styles.Icon} type={icon} />
      </SmartLink>
    )}
  </div>
)

InfoBlock.propTypes = {
  children: node,
  heading: string,
  ctaText: string,
  ctaUrl: string,
  icon: string
}

const ArticleEventInfo = ({
  _id,
  address,
  price,
  startDate,
  endDate,
  eventRegistrationLink
}) => {
  const formattedAddress = address.split(', ')
  const calendarCtaUrl = `/calendar-link?id=${encodeURIComponent(_id)}`

  return (
    <div className={styles.ArticleEventInfo}>
      <div className={styles.Header}>
        <Type size="body2" uppercase weight="bold">
          <span className={styles.muted}>event</span>
          <span>info</span>
        </Type>
      </div>
      <InfoBlock
        icon="calendar"
        heading="Date & Time"
        ctaText="Add to calendar"
        ctaUrl={calendarCtaUrl}
      >
        <Type size={['body2', 'heading8']} weight="medium" as="p">
          {format(parseISO(startDate), 'E d MMM')} -{' '}
          {format(parseISO(endDate), 'E d MMM, yyyy')}
        </Type>
        <Type size={['body2', 'heading8']} weight="medium" as="p">
          {format(parseISO(startDate), 'HH:mm')} -{' '}
          {format(parseISO(endDate), 'HH:mm')}
        </Type>
      </InfoBlock>
      {formattedAddress && (
        <InfoBlock
          icon="map-pin"
          heading="Location"
          ctaText="View on map"
          ctaUrl={`https://www.google.com/maps?q=${address}`}
        >
          <Type size={['body2', 'heading8']} weight="medium" as="p">
            {formattedAddress.map((item, i) => (
              <span key={`EventInfoBlock--FormattedAddress--${i}`}>
                {item}
                {i !== formattedAddress.length - 1 && <br />}
              </span>
            ))}
          </Type>
        </InfoBlock>
      )}
      {price && (
        <Hide below="desktop">
          <InfoBlock heading="Price">
            <Type size={['body2', 'heading8']} weight="medium" as="p">
              {price}
            </Type>
            <div className={styles.EventRegistrationButton}>
              <ArticleEventRegistration
                eventRegistrationLink={eventRegistrationLink}
              />
            </div>
          </InfoBlock>
        </Hide>
      )}
    </div>
  )
}

ArticleEventInfo.propTypes = {
  address: string,
  price: string,
  startDate: string,
  endDate: string,
  eventRegistrationLink: string,
  _id: string
}

export default ArticleEventInfo
